import React from "react";
import { NodeEdge } from "@hex-insights/app-modules";
import {
	Button,
	CalendarTypes,
	Column,
	compareObjects,
	compareStringsAsc,
	Conditional,
	dateTrunc,
	Defined,
	Else,
	FormattedText,
	getLastDayInMonth,
	getObjectValues,
	Heading,
	If,
	isDate,
	isSameTime,
	isString,
	makeClassName,
	mappedArray,
	Modal,
	Row,
	sort,
	stringToLocalDate,
	toGrammaticalNumber,
	toLocalDateString,
	Tooltip,
	TruncatedText,
	useToggle,
} from "@hex-insights/core";
import { CalendarEventUtilities } from "../../Models";
import * as CalendarEventComponents from "../../Models/Components/CalendarEvent";
import * as UserGroupComponents from "../../Models/Components/UserGroup";
import { CalendarEventFormat } from "../../Models/Format";
import { useBirthdaysQuery } from "../../Utilities";
import { BirthdayModal, BirthdayModalProps } from "../BirthdayModal";
import { Calendar, CalendarProps } from "../Calendar";
import { Icon } from "../Icon";
import { BigEventCalendar } from "./BigEventCalendar";
import { BirthdayEventAdditional, CalendarItemGroup, ControlledEventType, EventCalendarItem } from "./types";
import { dateToDateMapKey, getEventColor, getEventGroupName, rangeDatedItemsToDateMap } from "./utils";
import "./styles.css";
import styles from "./styles.module.css";

export * from "./BigEventCalendar";

export { getCalendarEventFiltersForViewerForDateRange } from "./utils";

export {
	type CalendarItemGroup,
	type EventCalendarItem,
	type EventTooltipProps as EventCalendarTooltipProps,
} from "./types";

export type EventCalendarProps = Omit<CalendarProps, "value" | "tileContent" | "onChange"> & {
	value: NonNullable<CalendarProps["value"]>;
	onChange?: (value: Date | null) => void;
	isLoading?: boolean;
	events?: EventCalendarItem[] | NodeEdge<EventCalendarItem>[];
	onClickEvent?: (event: Pick<EventCalendarItem, "id" | "name">) => void;
	numEventsInList?: number;
	withBirthdays?: boolean;
	containerClassName?: string;
	containerStyle?: React.CSSProperties;
};

export function EventCalendar({
	value,
	isLoading,
	events,
	onClickEvent,
	numEventsInList,
	withBirthdays = false,
	minDetail = "month",
	containerClassName,
	containerStyle,
	...props
}: EventCalendarProps) {
	const { onChange } = props;

	const dateEventMap = React.useMemo<Record<string, EventCalendarItem[]>>(() => {
		if (!events) {
			return {};
		}
		return rangeDatedItemsToDateMap(
			events,
			(e) => (e.isAllDay ? "startDate" : "startTime"),
			(e) => (e.isAllDay ? "endDate" : "endTime"),
			(e) => e.isAllDay,
		);
	}, [events]);

	const valueString = isDate(value) ? toLocalDateString(value) : value;
	const { data: birthdaysData } = useBirthdaysQuery({
		variables: {
			dates: [valueString],
			filters: [
				{
					hasEmployee: true,
					employee: [{ or: [{ endDateIsNull: true }, { endDateGTE: valueString }] }],
				},
				{
					hasStudent: true,
					student: [
						{
							studentAcademicTermEnrollments: [
								{ academicTerm: [{ startDateLTE: valueString, endDateGTE: valueString }] },
							],
						},
					],
				},
			],
		},
		skip: !withBirthdays,
	});

	const eventsOnSelectedDate = React.useMemo(() => {
		const events = sort(dateEventMap[valueString] ?? [], CalendarEventUtilities.compareCalendarEventsOnDate());
		if (!withBirthdays || !birthdaysData || birthdaysData.personConnectionByBirthday.edges.length === 0) {
			return events;
		}
		const birthdayEvents = birthdaysData.personConnectionByBirthday.edges.map((e) => ({
			id: `birthdays-${valueString}-${e.node.id}`,
			name: `${e.node.name}'s birthday!`,
			description: "",
			isAllDay: true,
			startTime: null,
			endTime: null,
			startDate: valueString,
			endDate: valueString,
			isPublic: false,
			groups: [
				{
					id: "birthdays",
					name: ControlledEventType.Birthdays,
					color: "var(--theme-colors__orange)",
					_isControlled: true,
				},
			],
			_isControlled: true,
			_additional: {
				name: e.node.name,
				shortName: e.node.givenName,
				dateOfBirth: stringToLocalDate(e.node.dateOfBirth!, "day"),
				isStudent: e.node.student !== null && e.node.employee === null && e.node.guardian === null,
			},
		}));
		return [...events, ...birthdayEvents];
	}, [valueString, dateEventMap, withBirthdays, birthdaysData]);

	const tileContent = React.useCallback<CalendarTypes.TileContentFunc>(
		({ date, view }) => {
			if (view !== "month") {
				return null;
			}
			return <EventCalendar.TileContent events={dateEventMap[dateToDateMapKey(date)] ?? []} />;
		},
		[dateEventMap],
	);

	const valueDate = React.useMemo(
		() => (isString(value) ? stringToLocalDate(value, "day") : value) ?? new Date(),
		[value],
	);

	const [valueBeforeModalOpen, setValueBeforeModalOpen] = React.useState(valueDate);
	const { isModalOpen, openModal: baseOpenModal, closeModal: baseCloseModal } = Modal.useToggle();
	const openModal = React.useCallback(() => {
		setValueBeforeModalOpen(valueDate);
		baseOpenModal();
	}, [valueDate, baseOpenModal]);
	const closeModal = React.useCallback(() => {
		if (onChange) {
			onChange(valueBeforeModalOpen);
		}
		baseCloseModal();
	}, [onChange, valueBeforeModalOpen, baseCloseModal]);

	return (
		<Column
			justify="spaced-start"
			className={containerClassName}
			style={
				{
					...containerStyle,
					position: "relative",
					zIndex: 1,
				} as React.CSSProperties
			}
		>
			<Row justify="center" style={{ width: "100%" }}>
				<div className="event-calendar__calendar-wrapper" style={{ width: "100%" }}>
					<div className="event-calendar__additional-menu">
						<Button variant="link" size="small" onClick={openModal} className="event-calendar__additional-menu__buton">
							<Row justify="center" align="center">
								<Icon.ArrowsOut size={22} className="block" />
							</Row>
						</Button>
					</div>

					<EventCalendar.Calendar
						value={value}
						tileContent={tileContent}
						minDetail={minDetail}
						withRoomForAdditionalButton
						{...props}
					/>

					<Modal.If condition={isModalOpen}>
						<Modal size="large" onClose={closeModal}>
							<Modal.Body style={{ height: "100%" }}>
								<BigEventCalendar value={valueDate} onChange={onChange} events={events} style={{ height: "100%" }} />
							</Modal.Body>
						</Modal>
					</Modal.If>
				</div>
			</Row>

			<EventCalendar.EventsList
				isLoading={isLoading}
				events={eventsOnSelectedDate}
				value={value}
				onClickEvent={onClickEvent}
				numEventsInList={numEventsInList}
			/>
		</Column>
	);
}

export type EventCalendarEventsListProps = Pick<
	EventCalendarProps,
	"isLoading" | "value" | "onClickEvent" | "numEventsInList"
> & {
	events: EventCalendarItem[];
	ListItem?: (props: EventCalendarEventListItemProps) => JSX.Element | null;
};

EventCalendar.EventsList = function ({
	isLoading,
	events,
	value,
	onClickEvent,
	numEventsInList = 3,
	ListItem = EventCalendar.EventListItem,
}: EventCalendarEventsListProps) {
	const { isOn: isFullEventListShown, toggle: toggleIsFullEventListShown } = useToggle(false);

	const valueDate = React.useMemo(
		() => (isString(value) ? stringToLocalDate(value, "day") : value) ?? new Date(),
		[value],
	);

	const wrappedOnClickEvent = React.useCallback(
		(event: Pick<EventCalendarItem, "id" | "name" | "_isControlled">) => {
			if (event._isControlled) {
				return true;
			}
			if (onClickEvent) {
				onClickEvent(event);
				return false;
			}
			return true;
		},
		[onClickEvent],
	);

	return (
		<div style={{ flexGrow: 1, width: "100%", height: "100%" }}>
			<Column justify="spaced-start" verticalSpacing="0.375rem" style={{ width: "100%", minWidth: 0 }}>
				<Conditional>
					<If condition={isLoading ?? false}>
						<EventCalendar.LoadingEventsListItem />
						{mappedArray(numEventsInList - 1, (i) => (
							<EventCalendar.FillerEventListItem key={i} />
						))}
					</If>
					<If condition={events.length === 0}>
						<EventCalendar.NoEventsListItem />
						{mappedArray(numEventsInList - 1, (i) => (
							<EventCalendar.FillerEventListItem key={i} />
						))}
					</If>
					<If condition={events.length <= numEventsInList}>
						{events.map((e) => (
							<ListItem key={e.id} calendarEvent={e} date={valueDate} onClick={() => wrappedOnClickEvent(e)} />
						))}
						{mappedArray(Math.max(numEventsInList - events.length, 0), (i) => (
							<EventCalendar.FillerEventListItem key={i} />
						))}
					</If>
					<Else>
						{events.slice(0, numEventsInList - 1).map((e) => (
							<ListItem key={e.id} calendarEvent={e} date={valueDate} onClick={() => wrappedOnClickEvent(e)} />
						))}
						<EventCalendar.ShowAllEventsListItem
							numMore={events.length - (numEventsInList - 1)}
							onClick={toggleIsFullEventListShown}
						/>
					</Else>
				</Conditional>
			</Column>

			<If condition={isFullEventListShown}>
				<Column justify="spaced-start" align="center" className={styles["event-calendar__full-event-list"]}>
					<Heading.H3 noMargin>
						{(value ?? new Date()).toLocaleString(undefined, {
							year: "numeric",
							month: "short",
							day: "numeric",
							weekday: "short",
						})}
					</Heading.H3>

					<div className={styles["event-calendar__full-event-list__list__wrapper"]}>
						<Column
							justify="spaced-start"
							verticalSpacing="0.375rem"
							className={styles["event-calendar__full-event-list__list"]}
						>
							{events.map((e) => (
								<ListItem key={e.id} calendarEvent={e} date={valueDate} onClick={() => wrappedOnClickEvent(e)} />
							))}
						</Column>
					</div>

					<Row justify="center">
						<Button variant="link" onClick={toggleIsFullEventListShown} style={{ paddingTop: 0, paddingBottom: 0 }}>
							Close
						</Button>
					</Row>
				</Column>
			</If>
		</div>
	);
};

export type EventCalendarCalendarProps = Omit<CalendarProps, "prevLabel" | "todayLabel" | "nextLabel"> & {
	withRoomForAdditionalButton?: boolean;
};

EventCalendar.Calendar = function ({ withRoomForAdditionalButton, className, ...props }: EventCalendarCalendarProps) {
	return (
		<Calendar
			prevLabel={
				<Row justify="center" align="center">
					<Icon.CaretLeft size={22} className="block" />
				</Row>
			}
			todayLabel={
				<Row justify="center" align="center" style={{ marginTop: "-0.25rem" }}>
					<Icon.DotOutline size={22} className="block" />
				</Row>
			}
			nextLabel={
				<Row justify="center" align="center">
					<Icon.CaretRight size={22} className="block" />
				</Row>
			}
			className={makeClassName(
				styles["event-calendar__calendar"],
				"event-calendar__calendar",
				withRoomForAdditionalButton ? "event-calendar__calendar--with-room-for-additional-button" : "",
				className,
			)}
			{...props}
		/>
	);
};

export type EventCalendarTileContentProps = {
	events: Pick<EventCalendarItem, "isPublic" | "groups">[];
	noTitle?: boolean;
};

EventCalendar.TileContent = function ({ events, noTitle = false }: EventCalendarTileContentProps) {
	const numEvents = events.length;

	const groups = React.useMemo(() => {
		const groupsMap: Record<string, Omit<CalendarItemGroup, "id">> = {};
		for (let i = 0; i < events.length; i++) {
			const event = events[i];
			const groupName = getEventGroupName(event);
			const groupColor = getEventColor(event);
			const groupKey = `${groupName}-${groupColor}`;
			groupsMap[groupKey] = { name: groupName, color: groupColor };
		}
		return getObjectValues(groupsMap).sort(compareObjects("name", compareStringsAsc()));
	}, [events]);

	const numGroups = groups.length;
	const maxGroups = 3;
	const hasAdditionalGroups = numGroups > maxGroups;
	const displayedGroups = hasAdditionalGroups ? groups.slice(0, maxGroups - 1) : groups;

	return (
		<div
			title={noTitle ? undefined : toGrammaticalNumber("Event", numEvents, true)}
			className={styles["event-calendar__tile-content"]}
		>
			<Row justify="spaced-center" horizontalSpacing="0.15rem" align="center">
				<Conditional>
					<If condition={numGroups > 0}>
						{displayedGroups.map((e, i) => (
							<div key={i} className={styles["event-calendar__event-icon"]} style={{ backgroundColor: e.color }} />
						))}
					</If>
					<Else>
						<div className={styles["event-calendar__event-icon"]} />
					</Else>
				</Conditional>
				<If condition={hasAdditionalGroups}>
					<Icon.Plus size={8} color="#aaa" />
				</If>
			</Row>
		</div>
	);
};

export type EventCalendarEventListItemProps = {
	calendarEvent: EventCalendarItem;
	date: Date;
	onClick?: () => boolean | void;
	IconComponent?: (props: Icon.IconProps) => JSX.Element | null;
	noTime?: boolean;
};

EventCalendar.EventListItem = function ({
	calendarEvent,
	date,
	onClick,
	IconComponent,
	noTime = false,
}: EventCalendarEventListItemProps) {
	const eventColor = getEventColor(calendarEvent);
	const groupName = getEventGroupName(calendarEvent);

	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	const isBirthdayEvent =
		calendarEvent.groups?.some((e) => e._isControlled && e.name === ControlledEventType.Birthdays) ?? false;
	IconComponent = IconComponent ?? (isBirthdayEvent ? Icon.Gift : Icon.CalendarBlank);
	const allowsOpenModal = !calendarEvent._isControlled || isBirthdayEvent;

	const birthdayModalProps = React.useMemo<BirthdayModalProps>(() => {
		if (!isBirthdayEvent) {
			return {
				name: "",
				shortName: "",
				dateOfBirth: new Date(),
				showAge: false,
			};
		}
		const additional = calendarEvent._additional as BirthdayEventAdditional;
		return {
			name: additional.name,
			shortName: additional.shortName,
			dateOfBirth: additional.dateOfBirth,
			showAge: additional.isStudent,
		};
	}, [isBirthdayEvent, calendarEvent._additional]);

	const fullOnClick = React.useCallback(() => {
		let continueWithDefault = true;
		if (onClick) {
			continueWithDefault = onClick() ?? true;
		}
		if (allowsOpenModal && continueWithDefault) {
			openModal();
		}
	}, [allowsOpenModal, openModal, onClick]);

	return (
		<React.Fragment>
			<Tooltip.Container
				openDelayMS={350}
				className={styles["event-calendar__event-list-item__tooltip-container"]}
				style={{ "--event-color": eventColor } as React.CSSProperties}
			>
				<div onClick={fullOnClick} className={styles["event-calendar__event-list-item"]}>
					<Row justify="space-between" align="center" style={{ position: "relative", zIndex: 1 }}>
						<Row
							justify="spaced-start"
							align="center"
							horizontalSpacing="0.5rem"
							className={styles["event-calendar__event-list-item__icon-name-container"]}
						>
							<IconComponent size={16} className={styles["event-calendar__event-list-item__icon"]} />
							<TruncatedText className={styles["event-calendar__event-list-item__name"]}>
								{calendarEvent.name}
							</TruncatedText>
						</Row>
						<If condition={!noTime}>
							<span className={styles["event-calendar__event-list-item__time"]}>
								{CalendarEventFormat.timeRangeOnDate(calendarEvent, date)}
							</span>
						</If>
					</Row>
				</div>

				<Tooltip className={styles["event-calendar__event-list-item__tooltip"]}>
					<Tooltip.Header className={styles["event-calendar__event-list-item__tooltip__header"]}>
						<div className={styles["event-calendar__event-list-item__tooltip__title"]}>
							<span style={{ position: "relative", zIndex: 1 }}>{calendarEvent.name}</span>
						</div>
					</Tooltip.Header>
					<Tooltip.Body className={styles["event-calendar__event-list-item__tooltip__body"]}>
						<Column justify="spaced-start" verticalSpacing="0.5rem">
							<If condition={!noTime}>
								<span className={styles["event-calendar__event-list-item__tooltip__time"]}>
									{CalendarEventFormat.timeRangeOnDate(calendarEvent, date)}
								</span>
							</If>

							<If condition={calendarEvent.description.length > 0}>
								<TruncatedText lines={2}>
									<FormattedText>{calendarEvent.description}</FormattedText>
								</TruncatedText>
							</If>

							<Conditional>
								<If condition={calendarEvent.isPublic || (calendarEvent.groups?.length ?? 0) === 0}>
									<Row justify="spaced-start" horizontalSpacing="0.25rem" align="center">
										<UserGroupComponents.UserGroupIcon userGroup={{ color: "var(--event-color)" }} size={12} />
										<span>{groupName}</span>
									</Row>
								</If>
								<Else>
									{calendarEvent.groups?.map((e) => (
										<Row key={e.name} justify="spaced-start" horizontalSpacing="0.25rem" align="center">
											<UserGroupComponents.UserGroupIcon userGroup={e} size={12} />
											<span>{e.name}</span>
										</Row>
									))}
								</Else>
							</Conditional>
						</Column>
					</Tooltip.Body>
				</Tooltip>
			</Tooltip.Container>

			<Conditional>
				<If condition={isBirthdayEvent}>
					<Modal.If condition={isModalOpen}>
						<BirthdayModal {...birthdayModalProps} relativeDate={date} onClose={closeModal} />
					</Modal.If>
				</If>
				<Else>
					<Modal.If condition={isModalOpen}>
						<CalendarEventComponents.ControlledCalendarEventModal
							calendarEventID={calendarEvent.id}
							onClose={closeModal}
						/>
					</Modal.If>
				</Else>
			</Conditional>
		</React.Fragment>
	);
};

EventCalendar.LoadingEventsListItem = function () {
	return (
		<div
			className={styles["event-calendar__event-list-item"] + " " + styles["event-calendar__event-list-item--loading"]}
		>
			<div className={styles["event-calendar__event-list-item__icon-name-container"]} style={{ lineHeight: "14px" }}>
				<div className={styles["event-calendar__event-list-item__name"]}>Loading...</div>
			</div>
		</div>
	);
};

EventCalendar.NoEventsListItem = function () {
	return (
		<div
			className={styles["event-calendar__event-list-item"] + " " + styles["event-calendar__event-list-item--no-events"]}
		>
			<div className={styles["event-calendar__event-list-item__icon-name-container"]} style={{ lineHeight: "14px" }}>
				<div className={styles["event-calendar__event-list-item__name"]}>No Events</div>
			</div>
		</div>
	);
};

EventCalendar.FillerEventListItem = function () {
	return (
		<div
			className={styles["event-calendar__event-list-item"] + " " + styles["event-calendar__event-list-item--filler"]}
		>
			<div className={styles["event-calendar__event-list-item__icon-name-container"]} style={{ lineHeight: "14px" }}>
				<div className={styles["event-calendar__event-list-item__name"]}>Filler</div>
			</div>
		</div>
	);
};

type EventCalendarShowAllEventsListItemProps = {
	numMore: number;
	onClick: () => void;
};

EventCalendar.ShowAllEventsListItem = function ({ numMore, onClick }: EventCalendarShowAllEventsListItemProps) {
	return (
		<div
			className={styles["event-calendar__event-list-item"] + " " + styles["event-calendar__event-list-item--show-all"]}
		>
			<div className={styles["event-calendar__event-list-item__icon-name-container"]} style={{ lineHeight: "14px" }}>
				<div className={styles["event-calendar__event-list-item__name"]}>
					<Button
						variant="link"
						size="small"
						onClick={onClick}
						style={{ paddingTop: 0, paddingBottom: 0, margin: "0 auto", display: "block" }}
					>
						+ {numMore} More
					</Button>
				</div>
			</div>
		</div>
	);
};

const monthNavActions = new Set(["onChange", "prev2", "prev", "next", "next2"]);

EventCalendar.useDates = function (initialDate = new Date()) {
	const [activeStartDate, setActiveStartDate] = React.useState(initialDate);
	const onActiveStartDateChange = React.useCallback<Defined<EventCalendarProps["onActiveStartDateChange"]>>(
		({ action, activeStartDate }) => {
			if (!monthNavActions.has(action)) {
				return;
			}
			setActiveStartDate(activeStartDate ?? new Date());
		},
		[],
	);
	const onClickMonth = React.useCallback<Defined<EventCalendarProps["onClickMonth"]>>((date) => {
		setActiveStartDate(date);
	}, []);

	const [value, setValue] = React.useState(initialDate);
	const onChange = React.useCallback<Defined<EventCalendarProps["onChange"]>>((date) => {
		const nextValue = date ?? new Date();
		setValue(nextValue);
		setActiveStartDate(dateTrunc(nextValue, "month").toDate());
	}, []);

	const firstOfMonth = dateTrunc(activeStartDate, "month").toDate();
	const lastOfMonth = dateTrunc(getLastDayInMonth(firstOfMonth), "day").toDate();

	const firstOfMonthStr = firstOfMonth.toISOString();
	React.useEffect(() => {
		const today = new Date();
		if (!isSameTime(firstOfMonthStr, value, "month")) {
			if (isSameTime(firstOfMonthStr, today, "month")) {
				setValue(today);
			} else {
				setValue(new Date(firstOfMonthStr));
			}
		}
	}, [firstOfMonthStr, value]);

	return { value, firstOfMonth, lastOfMonth, onActiveStartDateChange, onClickMonth, onChange };
};
